import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Send } from "lucide-react";
import { CheckinModes, InsightRow } from "@/components/reminders/ReminderInsightParts";

function ProposeReminderForm({ clientId, habits, onSent }) {
  const [propose, setPropose] = useState({ habit_id: habits[0]?.id || "", time: "08:00", note: "" });
  const [sending, setSending] = useState(false);
  const set = (patch) => setPropose((p) => ({ ...p, ...patch }));

  const send = async (e) => {
    e.preventDefault();
    if (!propose.habit_id || !propose.time) return;
    const habit = habits.find((h) => h.id === propose.habit_id);
    setSending(true);
    try {
      await api.post("/clinician/recommendation", {
        client_id: clientId,
        message: propose.note.trim() || `A reminder around ${propose.time} might make "${habit?.title}" easier to keep. Tap below to set it — adjust anything you like.`,
        reminder_habit_id: propose.habit_id,
        reminder_time: propose.time,
      });
      toast.success("Reminder suggestion sent. The member can set it with one tap.");
      set({ note: "" });
      onSent?.();
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} className="mt-4 pt-4 border-t border-stone-100 space-y-2" data-testid="propose-reminder-form">
      <div className="text-[10px] font-bold uppercase tracking-widest text-stone-600 flex items-center gap-1.5"><Send className="w-3 h-3" /> Propose a reminder time</div>
      <div className="flex gap-2 flex-wrap">
        <select data-testid="propose-habit" value={propose.habit_id} onChange={(e) => set({ habit_id: e.target.value })}
          className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border border-stone-300 text-sm bg-white focus:border-amber-600 outline-none">
          {habits.map((h) => <option key={h.id} value={h.id}>{h.title}</option>)}
        </select>
        <input data-testid="propose-time" type="time" value={propose.time} onChange={(e) => set({ time: e.target.value })}
          className="px-3 py-2 rounded-xl border border-stone-300 text-sm focus:border-amber-600 outline-none" />
        <button data-testid="propose-send" type="submit" disabled={sending} className="amber-btn text-xs disabled:opacity-50">Send</button>
      </div>
      <input data-testid="propose-note" value={propose.note} onChange={(e) => set({ note: e.target.value })} maxLength={300}
        placeholder="Optional note (a friendly default is used if blank)"
        className="w-full px-3 py-2 rounded-xl border border-stone-200 text-xs focus:border-amber-600 outline-none" />
    </form>
  );
}

export function ReminderInsights({ clientId, habits = [], onSent }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/clinician/client/${clientId}/reminders`).then((r) => setData(r.data));
  }, [clientId]);
  if (!data) return null;

  return (
    <div className="warm-card p-6" data-testid="reminder-insights">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-amber-700" />
        <h3 className="font-display font-bold text-stone-900">Reminder insights</h3>
      </div>
      <p className="text-xs text-stone-500 mb-4">Which nudges lead to action — and which get snoozed — so timing can be tuned together.</p>
      <CheckinModes modes={data.checkin_modes} />
      {data.reminders.length === 0 ? (
        <div className="text-sm text-stone-500">No reminders set up yet. You might suggest one for a habit that keeps slipping.</div>
      ) : (
        <ul className="space-y-2">{data.reminders.map((r) => <InsightRow key={r.id} r={r} />)}</ul>
      )}
      {habits.length > 0 && <ProposeReminderForm key={habits[0].id} clientId={clientId} habits={habits} onSent={onSent} />}
    </div>
  );
}
